import React, { ReactNode } from "react";
import { PivotField } from "../types";
import { FeatherX } from "@subframe/core";

interface DropZoneProps {
  title: string;
  description?: string;
  fields: PivotField[];
  onRemoveField: (fieldId: string) => void;
  onDrop: (field: any) => void;
  children?: ReactNode;
  className?: string;
}

export const DropZone: React.FC<DropZoneProps> = ({
  title,
  description,
  fields,
  onRemoveField,
  onDrop,
  children,
  className = "",
}) => {
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    try {
      const fieldData = e.dataTransfer.getData("application/json");
      if (fieldData) {
        const field = JSON.parse(fieldData);
        onDrop(field);
      }
    } catch (error) {
      console.error("Error parsing dropped field:", error);
    }
  };

  return (
    <div
      className={`border-2 border-dashed border-neutral-border rounded-md p-4 bg-neutral-50 min-h-[120px] ${className}`}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div className="mb-2">
        <h4 className="text-body-bold font-body-bold text-default-font">
          {title}
        </h4>
        {description && (
          <p className="text-caption font-caption text-subtext-color mt-1">
            {description}
          </p>
        )}
      </div>

      {/* Dropped Fields */}
      {fields.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {fields.map((field) => (
            <div
              key={field.id}
              className="flex items-center gap-1 px-2 py-1 rounded-md bg-brand-50 border border-brand-200"
            >
              <span className="text-caption font-caption text-default-font">
                {field.name}
              </span>
              <button
                onClick={() => onRemoveField(field.id)}
                className="text-subtext-color hover:text-default-font transition-colors"
              >
                <FeatherX className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {fields.length === 0 && (
        <div className="text-center py-4">
          <p className="text-caption font-caption text-subtext-color">
            Drop fields here
          </p>
        </div>
      )}

      {/* Additional children (for special zone features) */}
      {children}
    </div>
  );
};
